/**
 * Toast notification service
 * Provides simple toast notifications for user feedback
 */

const TOAST_DURATION = 5000; // 5 seconds
const TOAST_CONTAINER_ID = 'toast-container';

/**
 * Ensure toast container exists in the DOM
 */
function ensureContainer() {
  let container = document.getElementById(TOAST_CONTAINER_ID);
  if (!container) {
    container = document.createElement('div');
    container.id = TOAST_CONTAINER_ID;
    container.className = 'toast-container';
    document.body.appendChild(container);
  }
  return container;
}

/**
 * Create and show a toast notification
 * @param {string} message - The message to display
 * @param {string} type - Toast type: 'success', 'error', 'warning', 'info'
 * @param {number} duration - Duration in ms (default: 5000)
 */
function showToast(message, type = 'info', duration = TOAST_DURATION) {
  const container = ensureContainer();

  const toast = document.createElement('div');
  toast.className = `toast toast-${type}`;

  // Icon based on type
  const icons = {
    success: '✓',
    error: '✕',
    warning: '⚠',
    info: 'ℹ',
  };

  toast.innerHTML = `
    <span class="toast-icon">${icons[type] || icons.info}</span>
    <span class="toast-message">${escapeHtml(message)}</span>
    <button class="toast-close" aria-label="Close">&times;</button>
  `;

  // Close button handler
  const closeBtn = toast.querySelector('.toast-close');
  closeBtn.addEventListener('click', () => removeToast(toast));

  // Add to container
  container.appendChild(toast);

  // Trigger animation
  requestAnimationFrame(() => {
    toast.classList.add('toast-show');
  });

  // Auto remove after duration
  setTimeout(() => removeToast(toast), duration);

  return toast;
}

/**
 * Remove a toast with animation
 */
function removeToast(toast) {
  if (!toast || !toast.parentNode) return;

  toast.classList.remove('toast-show');
  toast.classList.add('toast-hide');

  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  }, 300); // Match CSS transition duration
}

/**
 * Escape HTML to prevent XSS
 */
function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

/**
 * Toast API
 */
export const toast = {
  /**
   * Show a success toast
   * @param {string} message
   * @param {number} duration
   */
  success(message, duration) {
    return showToast(message, 'success', duration);
  },

  /**
   * Show an error toast
   * @param {string} message
   * @param {number} duration
   */
  error(message, duration) {
    return showToast(message, 'error', duration);
  },

  /**
   * Show a warning toast
   * @param {string} message
   * @param {number} duration
   */
  warning(message, duration) {
    return showToast(message, 'warning', duration);
  },

  /**
   * Show an info toast
   * @param {string} message
   * @param {number} duration
   */
  info(message, duration) {
    return showToast(message, 'info', duration);
  },
};

export default toast;
